import { z } from "zod";

/** Remaining-uses header on every successful AI route and on the 429; mirrors `quota.remaining`. */
export const AI_REMAINING_HEADER = "x-ai-remaining";
/** When the oldest counted use leaves the rolling window (ISO 8601). */
export const AI_RESET_HEADER = "x-ai-reset";
/** The `code` of the 429 every AI route answers when the rolling-window limit is spent. */
export const AI_QUOTA_EXCEEDED_CODE = "ai_quota_exceeded";
/** Default uses per rolling 24 hours, per device and per signed-in user (env `AI_SCAN_DAILY_LIMIT`). */
export const AI_DEFAULT_DAILY_LIMIT = 25;

/** What is left of a rolling-24h AI allowance after this call. */
export const AiQuota = z.object({
  limit: z.number().int().positive(),
  remaining: z.number().int().nonnegative(),
  resetAt: z.iso.datetime(),
});
export type AiQuota = z.infer<typeof AiQuota>;

/** The 429 body: remaining is always 0, resetAt is when the next use frees up. */
export const AiQuotaExceeded = z.object({
  error: z.string(),
  code: z.literal(AI_QUOTA_EXCEEDED_CODE),
  limit: z.number().int().positive(),
  remaining: z.literal(0),
  resetAt: z.iso.datetime(),
});
export type AiQuotaExceeded = z.infer<typeof AiQuotaExceeded>;

/** What the vision model read off a wine label; every field is the model's reading, never a catalogue fact. */
export const LabelRead = z.object({
  producer: z.string().max(200),
  name: z.string().max(200),
  /** `null` for non-vintage; absent means unreadable. */
  vintage: z.number().int().min(1800).max(2100).nullable().optional(),
  appellation: z.string().max(200).optional(),
  region: z.string().max(200).optional(),
  country: z.string().max(100).optional(),
  grapes: z.array(z.string().min(1).max(100)).max(30).default([]),
  colour: z.string().max(40).optional(),
});
export type LabelRead = z.infer<typeof LabelRead>;

/** `POST /v1/scan/label`: the read, the model that made it and what is left of today's allowance. */
export const LabelScanResponse = z.object({
  read: LabelRead,
  model: z.string(),
  quota: AiQuota,
});
export type LabelScanResponse = z.infer<typeof LabelScanResponse>;

/** `code` of a 502 whose model answer did not parse as a LabelRead. */
export const LABEL_UNREADABLE_CODE = "label_unreadable";
/** The vision model could not be reached or refused the call: a try again later, not a bad photo. */
export const LABEL_FAILED_CODE = "label_failed";
